/**
 * i18next bootstrap.
 *
 * English, French and Arabic ship in the bundle; the active language drives the
 * document `dir`/`lang` attributes so the layout flips to RTL for Arabic.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import docsAr from "@/features/docs/locales/ar.json";
import docsEn from "@/features/docs/locales/en.json";
import docsFr from "@/features/docs/locales/fr.json";

import ar from "./locales/ar.json";
import en from "./locales/en.json";
import fr from "./locales/fr.json";

/** Languages the UI ships with, in display order. */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
] as const;

/** A supported language code. */
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/** Text direction for a language code (defaults to LTR for unknown codes). */
export function directionFor(code: string): "ltr" | "rtl" {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.dir ?? "ltr";
}

/** Apply `lang` and `dir` to the document root for the given language. */
export function applyDocumentLanguage(code: string): void {
  const root = globalThis.document?.documentElement;
  if (!root) {
    return;
  }
  root.lang = code;
  root.dir = directionFor(code);
}

/** Initialise i18next once; safe to call multiple times. */
export function setupI18n(initialLanguage?: string): typeof i18n {
  if (i18n.isInitialized) {
    return i18n;
  }
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en, docs: docsEn },
        fr: { translation: fr, docs: docsFr },
        ar: { translation: ar, docs: docsAr },
      },
      lng: initialLanguage,
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
      interpolation: { escapeValue: false },
      detection: { order: ["localStorage", "navigator"], caches: ["localStorage"] },
      returnNull: false,
    });
  applyDocumentLanguage(i18n.resolvedLanguage ?? "en");
  i18n.on("languageChanged", applyDocumentLanguage);
  return i18n;
}

export default i18n;
